// apps/scheduler-worker/src/hooks/useElapsedTimer.ts
import { useState, useEffect, useCallback } from 'react';
import { differenceInSeconds } from 'date-fns';

interface UseElapsedTimerOptions {
  startTime: string | null;
  isRunning: boolean;
}

interface ElapsedTime {
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
}

export function useElapsedTimer({ startTime, isRunning }: UseElapsedTimerOptions): ElapsedTime {
  const calculateElapsed = useCallback((): ElapsedTime => {
    if (!startTime || !isRunning) {
      return { hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 };
    }

    const start = new Date(startTime);
    const now = new Date();
    const totalSeconds = Math.max(0, differenceInSeconds(now, start));

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return { hours, minutes, seconds, totalSeconds };
  }, [startTime, isRunning]);

  const [elapsed, setElapsed] = useState<ElapsedTime>(calculateElapsed);

  useEffect(() => {
    if (!isRunning) {
      setElapsed({ hours: 0, minutes: 0, seconds: 0, totalSeconds: 0 });
      return;
    }

    // Update immediately
    setElapsed(calculateElapsed());

    // Update every second
    const interval = setInterval(() => {
      setElapsed(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, calculateElapsed]);

  return elapsed;
}

/**
 * Format elapsed time as HH:MM:SS
 */
export function formatElapsedTime(elapsed: ElapsedTime): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(elapsed.hours)}:${pad(elapsed.minutes)}:${pad(elapsed.seconds)}`;
}
